import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { bankReconciliationService } from '../../services/financial/bank-reconciliation';
import { XMarkIcon, CheckIcon, ArrowUpOnSquareIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const BankReconciliationModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('select_account');
  
  // Data states
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [reconciliationPeriod, setReconciliationPeriod] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [unmatchedTransactions, setUnmatchedTransactions] = useState({
    unmatchedJournalEntries: [],
    unmatchedBankTransactions: []
  });
  const [matches, setMatches] = useState([]);
  const [currentReconciliation, setCurrentReconciliation] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    currency: 'SAR'
  });

  // Load bank accounts
  useEffect(() => {
    const loadBankAccounts = async () => {
      if (!currentCompany?.id) return;
      
      try {
        setLoading(true);
        const accounts = await bankReconciliationService.getBankAccounts(currentCompany.id);
        setBankAccounts(accounts);
        
        // If no accounts exist, show the create form
        if (accounts.length === 0) {
          setShowCreateForm(true);
        }
      } catch (error) {
        console.error('Error loading bank accounts:', error);
        
        // More specific error handling
        if (error.code === 'permission-denied') {
          toast.error(t('bank_reconciliation.permission_denied', 'You don\'t have permission to access bank accounts'));
        } else if (error.code === 'unavailable') {
          toast.error(t('bank_reconciliation.service_unavailable', 'Banking service is temporarily unavailable'));
        } else {
          toast.error(t('bank_reconciliation.error_loading_accounts', 'Failed to load bank accounts'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadBankAccounts();
  }, [currentCompany?.id, t]);

  // Create new bank account
  const handleCreateBankAccount = async (e) => {
    e.preventDefault();
    if (!newAccountForm.accountName || !newAccountForm.accountNumber) {
      toast.error(t('payment_tracking.fill_required_fields'));
      return;
    }

    try {
      setLoading(true);
      await bankReconciliationService.addBankAccount({
        companyId: currentCompany.id,
        ...newAccountForm,
        createdBy: user.uid
      });
      
      // Reload accounts
      const accounts = await bankReconciliationService.getBankAccounts(currentCompany.id);
      setBankAccounts(accounts);
      setShowCreateForm(false);
      setNewAccountForm({ accountName: '', accountNumber: '', bankName: '', currency: 'SAR' });
      toast.success(t('bank_reconciliation.account_created', 'Bank account created successfully'));
    } catch (error) {
      console.error('Error creating bank account:', error);
      toast.error(t('bank_reconciliation.error_creating_account', 'Failed to create bank account'));
    } finally {
      setLoading(false);
    }
  };

  // Start reconciliation
  const handleStartReconciliation = async () => {
    if (!selectedAccount) {
      toast.error(t('bank_reconciliation.select_account_first', 'Please select a bank account first'));
      return;
    }

    try {
      setLoading(true);
      
      // Get unmatched transactions for the period
      const startDate = new Date(reconciliationPeriod.startDate);
      const endDate = new Date(reconciliationPeriod.endDate);
      
      const unmatched = await bankReconciliationService.getUnmatchedTransactions(
        currentCompany.id,
        selectedAccount.id,
        startDate,
        endDate
      );
      
      setUnmatchedTransactions(unmatched);
      
      // Create reconciliation record
      const reconciliationId = await bankReconciliationService.createReconciliation({
        companyId: currentCompany.id,
        bankAccountId: selectedAccount.id,
        reconciliationDate: new Date(),
        periodStart: startDate,
        periodEnd: endDate,
        createdBy: user.uid
      });
      
      setCurrentReconciliation({ id: reconciliationId });
      setStep('match_transactions');
      
    } catch (error) {
      console.error('Error starting reconciliation:', error);
      toast.error(t('invoices.error_starting'));
    } finally {
      setLoading(false);
    }
  };

  // Auto-match transactions
  const handleAutoMatch = () => {
    const autoMatches = [];
    
    unmatchedTransactions.unmatchedJournalEntries.forEach(journalEntry => {
      unmatchedTransactions.unmatchedBankTransactions.forEach(bankTx => {
        // Simple matching logic: same amount and date within 3 days
        const journalAmount = journalEntry.lineItems
          .filter(line => line.accountCode === '1000')
          .reduce((sum, line) => sum + ((line.debit || 0) - (line.credit || 0)), 0);
        
        const bankAmount = bankTx.amount || 0;
        const journalDate = journalEntry.date?.toDate?.() || new Date(journalEntry.date);
        const bankDate = bankTx.date?.toDate?.() || new Date(bankTx.date);
        const daysDiff = Math.abs((journalDate - bankDate) / (1000 * 60 * 60 * 24));
        
        if (Math.abs(journalAmount - bankAmount) < 0.01 && daysDiff <= 3) {
          autoMatches.push({
            journalEntry,
            bankTransaction: bankTx,
            matchType: 'auto',
            confidence: daysDiff === 0 ? 'high' : 'medium'
          });
        }
      });
    });

    setMatches(autoMatches);
    toast.success(t('invoices.auto_matches_found'));
  };

  // Remove match
  const handleRemoveMatch = (index) => {
    setMatches(prev => prev.filter((_, i) => i !== index));
  };

  // Finalize reconciliation
  const handleFinalizeReconciliation = async () => {
    if (!currentReconciliation) return;

    try {
      setLoading(true);
      
      // Save all matches
      for (const match of matches) {
        await bankReconciliationService.matchTransaction(
          currentReconciliation.id,
          match.bankTransaction.id,
          match.journalEntry.id,
          match.matchType
        );
      }
      
      // Finalize reconciliation
      await bankReconciliationService.finalizeReconciliation(currentReconciliation.id, user.uid);
      
      toast.success(t('invoices.reconciliation_completed'));
      onSuccess?.();
      onClose();
      
    } catch (error) {
      console.error('Error finalizing reconciliation:', error);
      toast.error(t('invoices.error_finalizing'));
    } finally {
      setLoading(false);
    }
  };

  // Upload bank statement
  const handleUploadStatement = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      
      // Simple CSV parsing
      const text = await file.text();
      const lines = text.split('\n');
      const transactions = [];
      
      for (let i = 1; i < lines.length; i++) { // Skip header
        const [date, description, amount, balance] = lines[i].split(',');
        if (date && amount) {
          transactions.push({
            date: new Date(date),
            description: description?.trim() || '',
            amount: parseFloat(amount) || 0,
            balance: parseFloat(balance) || 0,
            id: `bank_tx_${i}`
          });
        }
      }
      
      // Upload statement
      await bankReconciliationService.uploadBankStatement({
        companyId: currentCompany.id,
        bankAccountId: selectedAccount.id,
        statementDate: new Date(),
        transactions,
        uploadedBy: user.uid
      });
      
      toast.success(t('invoices.statement_uploaded'));
      
    } catch (error) {
      console.error('Error uploading statement:', error);
      toast.error(t('invoices.error_uploading'));
    } finally {
      setLoading(false);
    }
  };

  const getMatchedAmount = () => {
    return matches.reduce((sum, match) => {
      return sum + (match.bankTransaction.amount || 0);
    }, 0);
  };

  const getUnmatchedJournalAmount = () => {
    const matchedJournalIds = matches.map(m => m.journalEntry.id);
    return unmatchedTransactions.unmatchedJournalEntries
      .filter(entry => !matchedJournalIds.includes(entry.id))
      .reduce((sum, entry) => {
        return sum + entry.lineItems
          .filter(line => line.accountCode === '1000')
          .reduce((lineSum, line) => lineSum + ((line.debit || 0) - (line.credit || 0)), 0);
      }, 0);
  };

  const getUnmatchedBankAmount = () => {
    const matchedBankIds = matches.map(m => m.bankTransaction.id);
    return unmatchedTransactions.unmatchedBankTransactions
      .filter(tx => !matchedBankIds.includes(tx.id))
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('bank_reconciliation.title', 'Bank Reconciliation')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <LoadingSpinner />
          </div>
        )}

        {/* Steps indicator */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center space-x-4">
            {['select_account', 'match_transactions', 'finalize'].map((stepName, index) => (
              <div key={stepName} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === stepName 
                    ? 'bg-blue-600 text-white' 
                    : index < ['select_account', 'match_transactions', 'finalize'].indexOf(step)
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  {index < ['select_account', 'match_transactions', 'finalize'].indexOf(step) ? (
                    <CheckIcon className="h-5 w-5" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {t(`bank_reconciliation.step_${stepName}`, stepName.replace('_', ' '))}
                </span>
                {index < 2 && <div className="ml-4 w-8 h-0.5 bg-gray-300" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Select Account */}
          {step === 'select_account' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">
                  {t('bank_reconciliation.select_account', 'Select Bank Account')}
                </h3>
                
                {bankAccounts.length === 0 && !showCreateForm ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      {t('bank_reconciliation.no_accounts', 'No bank accounts found')}
                    </p>
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <PlusIcon className="h-4 w-4 inline mr-1" />
                      {t('bank_reconciliation.create_account', 'Create Bank Account')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {bankAccounts.map(account => (
                        <div
                          key={account.id}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedAccount?.id === account.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedAccount(account)}
                        >
                          <h4 className="font-medium">{account.accountName}</h4>
                          <p className="text-sm text-gray-600">{account.bankName}</p>
                          <p className="text-sm text-gray-500">
                            {t('bank_reconciliation.account_number', 'Account:')} {account.accountNumber}
                          </p>
                        </div>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <PlusIcon className="h-4 w-4 inline mr-1" />
                      {t('bank_reconciliation.add_account', 'Add New Account')}
                    </button>
                  </>
                )}

                {/* Create Account Form */}
                {showCreateForm && (
                  <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                    <h4 className="font-medium mb-4">
                      {t('bank_reconciliation.create_new_account', 'Create New Bank Account')}
                    </h4>
                    <form onSubmit={handleCreateBankAccount} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('bank_reconciliation.account_name', 'Account Name')} *
                          </label>
                          <input
                            type="text"
                            value={newAccountForm.accountName}
                            onChange={(e) => setNewAccountForm(prev => ({ ...prev, accountName: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('bank_reconciliation.account_number', 'Account Number')} *
                          </label>
                          <input
                            type="text"
                            value={newAccountForm.accountNumber}
                            onChange={(e) => setNewAccountForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.bank_name')}
                        </label>
                        <input
                          type="text"
                          value={newAccountForm.bankName}
                          onChange={(e) => setNewAccountForm(prev => ({ ...prev, bankName: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex space-x-3">
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          {t('common.create', t('common.create'))}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCreateForm(false)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          {t('common.cancel', t('common.cancel'))}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {selectedAccount && (
                <div className="space-y-4">
                  <h4 className="font-medium">
                    {t('bank_reconciliation.reconciliation_period', 'Reconciliation Period')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.start_date', 'Start Date')}
                      </label>
                      <input
                        type="date"
                        value={reconciliationPeriod.startDate}
                        onChange={(e) => setReconciliationPeriod(prev => ({
                          ...prev,
                          startDate: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.end_date', 'End Date')}
                      </label>
                      <input
                        type="date"
                        value={reconciliationPeriod.endDate}
                        onChange={(e) => setReconciliationPeriod(prev => ({
                          ...prev,
                          endDate: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={handleStartReconciliation}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t('bank_reconciliation.start_reconciliation', 'Start Reconciliation')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Match Transactions */}
          {step === 'match_transactions' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  {t('bank_reconciliation.match_transactions', 'Match Transactions')}
                </h3>
                <div className="flex space-x-3">
                  <button
                    onClick={handleAutoMatch}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <MagnifyingGlassIcon className="h-4 w-4 inline mr-1" />
                    {t('invoices.auto_match')}
                  </button>
                  <label className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 cursor-pointer">
                    <ArrowUpOnSquareIcon className="h-4 w-4 inline mr-1" />
                    {t('invoices.upload_statement')}
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleUploadStatement}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{matches.length}</div>
                  <div className="text-sm text-gray-600">
                    {t('invoices.matched_transactions')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {new Intl.NumberFormat('ar-SA').format(getMatchedAmount())}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('invoices.matched_amount')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {unmatchedTransactions.unmatchedJournalEntries.length - matches.length}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('invoices.unmatched_journal')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {unmatchedTransactions.unmatchedBankTransactions.length - matches.length}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('invoices.unmatched_bank')}
                  </div>
                </div>
              </div>

              {/* Matches */}
              {matches.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">
                    {t('invoices.matched_transactions')}
                  </h4>
                  <div className="space-y-2">
                    {matches.map((match, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium">{match.journalEntry.description}</span>
                              <span className="ml-2 text-sm text-gray-600">
                                {match.bankTransaction.description}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                {new Intl.NumberFormat('ar-SA').format(match.bankTransaction.amount)} {t('common.currency', 'SAR')}
                              </div>
                              <div className="text-sm text-gray-600">
                                {match.matchType} - {match.confidence}
                              </div>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveMatch(index)}
                          className="ml-3 p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Transactions */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3">
                    {t('invoices.unmatched_journal_entries')}
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {unmatchedTransactions.unmatchedJournalEntries
                      .filter(entry => !matches.some(m => m.journalEntry.id === entry.id))
                      .map(entry => (
                        <div key={entry.id} className="p-3 border border-gray-200 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">{entry.description}</div>
                              <div className="text-sm text-gray-600">{entry.entryNumber}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                {new Intl.NumberFormat('ar-SA').format(
                                  entry.lineItems
                                    .filter(line => line.accountCode === '1000')
                                    .reduce((sum, line) => sum + ((line.debit || 0) - (line.credit || 0)), 0)
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-3">
                    {t('invoices.unmatched_bank_transactions')}
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {unmatchedTransactions.unmatchedBankTransactions
                      .filter(tx => !matches.some(m => m.bankTransaction.id === tx.id))
                      .map(tx => (
                        <div key={tx.id} className="p-3 border border-gray-200 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">{tx.description}</div>
                              <div className="text-sm text-gray-600">
                                {tx.date?.toDate?.()?.toLocaleDateString() || new Date(tx.date).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                {new Intl.NumberFormat('ar-SA').format(tx.amount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('select_account')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.back', 'Back')}
                </button>
                <button
                  onClick={() => setStep('finalize')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.next', 'Next')}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Finalize */}
          {step === 'finalize' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">
                {t('bank_reconciliation.finalize_reconciliation', 'Finalize Reconciliation')}
              </h3>

              {/* Final Summary */}
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-3">
                    {t('invoices.reconciliation_summary')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{t('invoices.matched_transactions')}:</span>
                      <span className="font-medium">{matches.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('invoices.matched_amount')}:</span>
                      <span className="font-medium">
                        {new Intl.NumberFormat('ar-SA').format(getMatchedAmount())} {t('common.currency', 'SAR')}
                      </span>
                    </div>
                    <div className="flex justify-between text-orange-600">
                      <span>{t('invoices.unmatched_journal_amount')}:</span>
                      <span className="font-medium">
                        {new Intl.NumberFormat('ar-SA').format(getUnmatchedJournalAmount())} {t('common.currency', 'SAR')}
                      </span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>{t('invoices.unmatched_bank_amount')}:</span>
                      <span className="font-medium">
                        {new Intl.NumberFormat('ar-SA').format(getUnmatchedBankAmount())} {t('common.currency', 'SAR')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-3">
                    {t('invoices.account_details')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>{t('bank_reconciliation.account_name', 'Account')}:</strong> {selectedAccount?.accountName}</div>
                    <div><strong>{t('invoices.period')}:</strong> {reconciliationPeriod.startDate} to {reconciliationPeriod.endDate}</div>
                    <div><strong>{t('invoices.reconciled_by')}:</strong> {user?.email}</div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('match_transactions')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.back', 'Back')}
                </button>
                <button
                  onClick={handleFinalizeReconciliation}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <CheckIcon className="h-4 w-4 inline mr-1" />
                  {t('bank_reconciliation.complete_reconciliation', 'Complete Reconciliation')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankReconciliationModal; 